import { useEffect, useState } from "react";
import { api, formatBRL } from "../../lib/api";
import { Sparkles, CreditCard, CheckCircle2, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function Billing() {
  const [me, setMe] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(null);

  const load = () => Promise.all([
    api.get("/saas/me").then(r => setMe(r.data)),
    api.get("/saas/plans-catalog").then(r => setPlans(r.data)),
  ]);
  useEffect(() => { load(); }, []);

  // If returning from Stripe with session_id, poll status
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session_id");
    if (!sid) return;
    let attempts = 0;
    const poll = async () => {
      attempts++;
      try {
        const { data } = await api.get(`/saas/checkout/status/${sid}`);
        if (data.payment_status === "paid") {
          toast.success("Pagamento confirmado! Assinatura ativada.");
          window.history.replaceState({}, "", "/admin/billing");
          load();
          return;
        }
        if (data.status === "expired" || attempts > 8) {
          toast.error("Sessão de pagamento expirou ou não foi concluída.");
          return;
        }
        setTimeout(poll, 2500);
      } catch {
        if (attempts < 8) setTimeout(poll, 3000);
      }
    };
    poll();
  }, []);

  const startCheckout = async (planId) => {
    setLoading(planId);
    try {
      const { data } = await api.post("/saas/checkout", { plan_id: planId, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao iniciar checkout");
      setLoading(null);
    }
  };

  if (!me) return <div className="text-gray-500">Carregando…</div>;

  return (
    <div data-testid="admin-billing">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.18em] text-[#D4AF37] font-semibold mb-2">Assinatura SaaS</div>
        <h1 className="font-display text-3xl text-[#14213D] font-light">Plano & cobrança</h1>
      </div>

      {/* Current status */}
      <div className={`mb-6 rounded-lg p-5 border ${me.is_active_subscription ? "bg-emerald-50 border-emerald-200" : me.is_trial ? "bg-amber-50 border-amber-200" : "bg-gray-50 border-gray-200"}`} data-testid="billing-status">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-wider font-semibold mb-1" style={{ color: me.is_active_subscription ? "#047857" : me.is_trial ? "#92400E" : "#374151" }}>
              {me.is_active_subscription ? "Assinatura ativa" : me.is_trial ? "Trial em andamento" : "Sem assinatura ativa"}
            </div>
            <div className="font-display text-xl text-[#14213D]">{me.tenant?.company_name}</div>
            <div className="text-sm text-gray-600 mt-1">
              Plano atual: <span className="font-semibold uppercase">{me.tenant?.plan}</span>
              {me.is_trial && me.trial_days_left != null && (
                <span className="ml-2">· <strong className={me.trial_days_left <= 3 ? "text-red-600" : "text-amber-700"}>{me.trial_days_left} dias restantes</strong></span>
              )}
            </div>
          </div>
          <Sparkles size={24} className="text-[#D4AF37]" />
        </div>
      </div>

      {/* Plans */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {plans.map((p) => {
          const isCurrent = me.tenant?.plan === p.id;
          const isEnterprise = p.id === "enterprise";
          return (
            <div key={p.id} className={`bg-white border rounded-lg p-6 flex flex-col ${p.highlight ? "border-[#D4AF37] shadow-lg" : "border-gray-200"}`} data-testid={`billing-plan-${p.id}`}>
              {p.highlight && (
                <div className="inline-flex items-center gap-1 self-start mb-3 px-2.5 py-0.5 bg-[#D4AF37] text-[#0A1128] text-[10px] uppercase tracking-wider font-bold rounded-full">
                  Mais escolhido
                </div>
              )}
              <div className="font-display text-2xl text-[#14213D] mb-1">{p.name}</div>
              <div className="mb-5">
                {p.price_monthly ? (
                  <>
                    <span className="font-display text-4xl text-[#14213D]">{formatBRL(p.price_monthly)}</span>
                    <span className="text-sm text-gray-500">/mês</span>
                  </>
                ) : (
                  <span className="font-display text-2xl text-[#14213D]">Sob consulta</span>
                )}
              </div>
              <ul className="space-y-2 mb-6 flex-1">
                {p.features.map((f, i) => (
                  <li key={i} className="flex gap-2 text-sm text-gray-700"><CheckCircle2 size={14} className="text-[#D4AF37] mt-0.5 flex-shrink-0" />{f}</li>
                ))}
              </ul>
              {isCurrent ? (
                <button disabled className="w-full bg-gray-100 text-gray-500 py-2.5 rounded-md text-sm font-semibold cursor-not-allowed">Plano atual</button>
              ) : isEnterprise ? (
                <a
                  href="https://wa.me/5521975968405?text=Olá!%20Tenho%20interesse%20no%20plano%20Enterprise."
                  target="_blank" rel="noreferrer"
                  className="w-full bg-[#14213D] text-white py-2.5 rounded-md text-sm font-semibold text-center hover:bg-[#1F335E]"
                >Falar com vendas</a>
              ) : (
                <button
                  onClick={() => startCheckout(p.id)}
                  disabled={loading === p.id}
                  className="w-full bg-[#D4AF37] text-[#14213D] py-2.5 rounded-md text-sm font-bold hover:bg-[#F3DFA2] disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid={`checkout-${p.id}`}
                >
                  {loading === p.id ? "Redirecionando…" : <><CreditCard size={14} /> Assinar agora <ArrowRight size={14} /></>}
                </button>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-6 bg-amber-50 border border-amber-200 rounded-lg p-4 text-xs text-amber-900">
        <strong>Stripe Checkout:</strong> ao clicar em &ldquo;Assinar agora&rdquo; você será redirecionado para o ambiente seguro do Stripe.
        Pagamentos em BRL, com PIX e cartão. Cancele quando quiser pelo Portal do Cliente Stripe.
      </div>
    </div>
  );
}
